import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Award, ShoppingBag } from 'lucide-react';
import ContactForm from '../components/ContactForm';

const Home = () => {
  const categories = [
    {
      id: 'skincare',
      name: 'Nuskay Skincare',
      description: 'Natural skincare reflecting the inner beauty within every woman',
      image: 'https://images.pexels.com/photos/6847836/pexels-photo-6847836.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      id: 'apparel',
      name: 'Nanaki Art x Apparel',
      description: 'Wearable art featuring monthly artist collaborations',
      image: 'https://images.unsplash.com/photo-1601597565151-70c4020dc0e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMGZhc2hpb258ZW58MHx8fHwxNzc1NDIxNjc4fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 'furniture',
      name: 'Furniture by MMC',
      description: 'Handcrafted solid wood furniture from MMC Wood Creations',
      image: 'https://mmcwoodcreations.com/cdn/shop/files/MMC_WOOD_CREATIONS.webp?v=1762836827&width=1200'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Image — first sight */}
      <section className="bg-white" data-testid="home-hero">
        <img
          src="https://customer-assets.emergentagent.com/job_kaur-studio/artifacts/m5cyiwl7_B0B91E85-BDA4-4F3E-B917-3C5C4B4D17DD.png"
          alt="Empowering Women. Building Community."
          className="w-full h-auto object-contain mx-auto"
          loading="eager"
        />
      </section>

      {/* CTA strip — sits between the two hero images, creating breathing room */}
      <section className="bg-white py-10 md:py-14" data-testid="home-cta-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3 md:gap-4">
            <Link
              to="/products"
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 md:px-8 py-3 rounded-lg font-semibold transition flex items-center justify-center space-x-2"
              data-testid="shop-now-button"
            >
              <ShoppingBag className="w-5 h-5" />
              <span>Shop Now</span>
            </Link>
            <Link
              to="/register-angel"
              className="bg-white border border-purple-600 hover:bg-purple-50 text-purple-600 px-6 md:px-8 py-3 rounded-lg font-semibold transition flex items-center justify-center space-x-2"
              data-testid="become-angel-button"
            >
              <span>Become a Nanaki's Kaur</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/showcase-brand"
              className="bg-white border border-purple-600 hover:bg-purple-50 text-purple-600 px-6 md:px-8 py-3 rounded-lg font-semibold transition flex items-center justify-center space-x-2"
              data-testid="showcase-brand-button"
            >
              <span>Showcase your brand</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/submit-art"
              className="bg-white border border-purple-600 hover:bg-purple-50 text-purple-600 px-6 md:px-8 py-3 rounded-lg font-semibold transition flex items-center justify-center space-x-2"
              data-testid="submit-art-button"
            >
              <span>Submit your Art</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/donate"
              className="bg-white border border-purple-600 hover:bg-purple-50 text-purple-600 px-6 md:px-8 py-3 rounded-lg font-semibold transition flex items-center justify-center space-x-2"
              data-testid="donate-button"
            >
              <span>Donate to Drishty Foundation</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Brand Ecosystem Poster — second sight, separated from hero by CTA strip */}
      <section className="bg-white pb-10 md:pb-14" data-testid="home-ecosystem-poster">
        <img
          src="https://customer-assets.emergentagent.com/job_kaur-studio/artifacts/39047zmc_1000039082.jpg"
          alt="Rooted in Faith. Driven by Purpose. — The Nanaki Ecosystem — Commerce with Purpose, Impact for Generations."
          className="w-full h-auto object-contain mx-auto"
          loading="lazy"
        />
      </section>

      {/* Our Brands */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Our Brands</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Discover our curated collection of products from our featured brands,
            each reflecting excellence and empowerment
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/products?category=${category.id}`}
                className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition"
                data-testid={`category-card-${category.id}`}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-purple-600 transition">
                    {category.name}
                  </h3>
                  <p className="text-gray-600 text-sm">{category.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Philosophy</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Inspired by the ethereal beauty of the heavens and grounded in the resilience of the Earth,
              our specially curated platform is designed to celebrate the surreal beauty of female creation
              while honoring the true community values of Sikhism. It stands as a tribute to empowerment,
              fostering growth, unity, and upliftment within the Kaur community.
            </p>
          </div>
          
          {/* Spiritual Unity Image */}
          <div className="mt-12 max-w-4xl mx-auto">
            <img
              src="https://customer-assets.emergentagent.com/job_kaur-studio/artifacts/uqmgo6jr_IMG_9996.webp"
              alt="Spiritual unity and community - Guru Nanak Dev Ji with diverse community in harmony"
              className="w-full h-auto rounded-lg shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* CSR & ESG */}
      <section className="py-16 bg-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">CSR & ESG</h2>
              <p className="text-gray-700 mb-6">
                Our Corporate Social Responsibility program works with the vision to provide
                <span className="font-semibold"> education, training, employment & eventual self-sustaining empowerment </span>
                to the <span className="font-semibold">female community</span> through Drishty Foundation.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Heart className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg">5% Commission</h3>
                    <p className="text-gray-600">Nanaki's Kaurs earn 5% commission on every sale</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Users className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg">Community Driven</h3>
                    <p className="text-gray-600">Creating a sustainable ecosystem for female empowerment</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Award className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg">Fair Trade</h3>
                    <p className="text-gray-600">100% female-oriented design, manufacturing & marketing</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1589104760192-ccab0ce0d90f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHx3b21lbiUyMGVkdWNhdGlvbnxlbnwwfHx8fDE3NzU0MjE2ODF8MA&ixlib=rb-4.1.0&q=85"
                alt="Women Empowerment"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Nanaki's Kaurs CTA */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Join the Nanaki's Kaurs</h2>
          <p className="text-xl mb-8 opacity-90">
            Earn 5% commission on every sale you refer. Get your unique referral code
            and start earning while supporting women-led businesses.
          </p>
          <Link
            to="/register-angel"
            className="inline-block bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
            data-testid="join-angels-button"
          >
            Sign Up Now
          </Link>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-4xl font-bold mb-6">Get in Touch</h2>
              <p className="text-gray-600 mb-8 text-lg">
                Have questions about our products or the Nanaki's Kaurs program? 
                We'd love to hear from you!
              </p>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg mb-2">Contact us</h3>
                  <a href="mailto:jasjeevbhasin5757@gmail.com" className="text-purple-600 hover:underline" data-testid="contact-email-link">
                    jasjeevbhasin5757@gmail.com
                  </a>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Phone</h3>
                  <a href="tel:+919930111823" className="text-purple-600 hover:underline">
                    +91 99-30-11-18-23
                  </a>
                </div>
              </div>
            </div>
            <div>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;